import { useLocale } from '../../i18n/LocaleProvider';
import type { OpsRedis } from '../../lib/infrastructure-api';
import {
  formatBytes,
  formatNumber,
  formatPercent,
} from '../../lib/infrastructure';
import {
  LevelBadge,
  Meter,
  Section,
  Sparkline,
  Stat,
} from './InfrastructureParts';

/**
 * Redis memory. It runs `noeviction` and is shared with BullMQ, so "full" does not mean slower —
 * it means no new jobs. That is why it gets a meter against `maxmemory`, not just a number.
 */
export function RedisPanel({ redis }: { readonly redis: OpsRedis | null }) {
  const { t, locale } = useLocale();
  if (!redis) {
    return (
      <Section title={t('infrastructure.redis.title')}>
        <p className="text-sm text-gray-600">
          {t('infrastructure.redis.unavailable')}
        </p>
      </Section>
    );
  }
  return (
    <Section
      title={t('infrastructure.redis.title')}
      lead={t('infrastructure.redis.note')}
      aside={<LevelBadge level={redis.level} />}
    >
      <div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-2xl font-bold text-gray-900">
            {formatBytes(redis.usedBytes, locale)}
          </span>
          <span className="text-sm text-gray-500">
            {redis.maxBytes === null
              ? t('infrastructure.redis.noLimit')
              : `${formatPercent(redis.usedPercent, locale)} · ${formatBytes(redis.maxBytes, locale)}`}
          </span>
        </div>
        <div className="mt-2">
          <Meter
            percent={redis.usedPercent}
            level={redis.level}
            label={t('infrastructure.redis.used')}
          />
        </div>
      </div>
      <dl className="grid grid-cols-3 gap-3">
        <Stat
          label={t('infrastructure.redis.peak')}
          value={formatBytes(redis.peakBytes, locale)}
        />
        <Stat
          label={t('infrastructure.redis.max')}
          value={
            redis.maxBytes === null
              ? t('infrastructure.redis.noLimit')
              : formatBytes(redis.maxBytes, locale)
          }
        />
        <Stat
          label={t('infrastructure.redis.clients')}
          value={formatNumber(redis.connectedClients, locale)}
        />
      </dl>
      <Sparkline
        title={t('infrastructure.redis.history')}
        points={redis.history.map((p) => ({ t: p.t, value: p.usedBytes }))}
        format={(v) => formatBytes(v, locale)}
      />
    </Section>
  );
}
